import { ProgressSpinner } from "primereact/progressspinner";
import React from "react";
import { Link } from "react-router-dom";

/**
 * The forecast grid shared by the create and edit screens.
 *
 * `rows` are the product lines; every row carries a `segments` array whose
 * indexes line up with `segmentDates`, so adding or removing a segment column
 * has to touch both.
 */
const ForecastForm = ({
  title,
  submitLabel,
  code,
  inputs,
  rows,
  segmentDates,
  loading,
  error,
  onInputChange,
  onRowChange,
  onSegmentQtyChange,
  onSegmentDateChange,
  onAddSegment,
  onRemoveSegment,
  onSubmit,
}) => {
  const displayError = (fieldName) => {
    if (error && error[fieldName]) {
      return <div className="text-danger small">{error[fieldName]}</div>;
    }
    return null;
  };

  return (
    <div className="am-mainpanel">
      <div className="am-pagetitle">
        <h5 className="am-title">{title}</h5>
        <Link to="/demand-forecast" className="btn btn-secondary">
          <i className="fa fa-arrow-left mg-r-6"></i> Back to List
        </Link>
      </div>

      <div className="am-pagebody">
        <div className="card pd-20 pd-sm-40">
          {loading ? (
            <ProgressSpinner />
          ) : (
            <>
              <div className="row">
                <div className="col-lg">
                  <label>
                    Start Date <i className="fa fa-star required"></i>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    name="start_date"
                    value={inputs.start_date || ""}
                    onChange={onInputChange}
                  />
                  {displayError("start_date")}
                </div>
                <div className="col-lg">
                  <label>
                    End Date <i className="fa fa-star required"></i>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    name="end_date"
                    value={inputs.end_date || ""}
                    onChange={onInputChange}
                  />
                  {displayError("end_date")}
                </div>
                <div className="col-lg">
                  <label>
                    Forecasting Month <i className="fa fa-star required"></i>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="forecasting_duration"
                    value={inputs.forecasting_duration || ""}
                    onChange={onInputChange}
                  />
                  {displayError("forecasting_duration")}
                </div>
                {code ? (
                  <div className="col-lg">
                    <label>Code</label>
                    <input type="text" className="form-control" value={code} disabled />
                  </div>
                ) : null}
              </div>

              <div className="row mt-3">
                <div className="col-12 d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Products for Demand Forecasting</h6>
                  <button type="button" className="btn btn-sm btn-info" onClick={onAddSegment}>
                    <i className="fa fa-plus"></i> Add Segment Column
                  </button>
                </div>
              </div>

              <div className="table-responsive mt-3">
                <table className="table table-sm table-bordered" style={{ fontSize: "12px" }}>
                  <thead>
                    <tr>
                      <th>Product Code</th>
                      <th>Product Name</th>
                      <th>Pack Size</th>
                      <th>Weight</th>
                      <th>Length</th>
                      <th>Height</th>
                      <th>Width</th>
                      <th>Price</th>
                      <th>DP Ctn/Bag</th>
                      {segmentDates.map((segment, index) => (
                        <th key={index} style={{ minWidth: "150px" }}>
                          <div className="d-flex justify-content-between align-items-center">
                            <strong>Segment {index + 1}</strong>
                            <button
                              type="button"
                              className="btn btn-xs btn-danger"
                              onClick={() => onRemoveSegment(index)}
                            >
                              <i className="fa fa-times"></i>
                            </button>
                          </div>
                          <label style={{ fontSize: "10px", fontWeight: "normal" }}>Start Date</label>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            style={{ fontSize: "10px" }}
                            value={segment.start_date || ""}
                            onChange={(e) => onSegmentDateChange(index, "start_date", e.target.value)}
                          />
                          {displayError(`segment_dates.${index}.start_date`)}
                          <label style={{ fontSize: "10px", fontWeight: "normal" }}>End Date</label>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            style={{ fontSize: "10px" }}
                            value={segment.end_date || ""}
                            onChange={(e) => onSegmentDateChange(index, "end_date", e.target.value)}
                          />
                          {displayError(`segment_dates.${index}.end_date`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length < 1 && (
                      <tr>
                        <td colSpan={9 + segmentDates.length}>
                          <p className="text-center mt-3 text-danger">No finished-goods products found.</p>
                        </td>
                      </tr>
                    )}

                    {rows.map((row, rowIndex) => (
                      <tr key={row.product_id}>
                        <td>{row.code}</td>
                        <td>{row.name}</td>
                        <td>{row.pack_size || "-"}</td>
                        {["weight", "length", "height", "width"].map((field) => (
                          <td key={field}>
                            <input
                              type="number"
                              step="0.01"
                              className="form-control form-control-sm"
                              style={{ width: "80px" }}
                              value={row[field] ?? ""}
                              onChange={(e) => onRowChange(rowIndex, field, e.target.value)}
                            />
                          </td>
                        ))}
                        <td>{Number(row.price || 0).toFixed(2)}</td>
                        <td>{Number(row.dp_ctn_bag || 0).toFixed(2)}</td>
                        {segmentDates.map((_, segIndex) => (
                          <td key={segIndex}>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className="form-control form-control-sm"
                              style={{ width: "100px" }}
                              value={row.segments[segIndex] ?? 0}
                              onChange={(e) => onSegmentQtyChange(rowIndex, segIndex, e.target.value)}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="row mt-3">
                <div className="col-3">
                  <button className="btn btn-primary btn-block mg-b-10" onClick={onSubmit}>
                    <i className="fa fa-save mg-r-10"></i> {submitLabel}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForecastForm;
